import React, { useState } from 'react'
import { Box, Button, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import DescriptionIcon from '@mui/icons-material/Description'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import { VisibilityOffTwoTone, VisibilityTwoTone } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { apiConstants, getGeneric } from '../../../common/constants'
import { formatDate, maskPhoneNumber, parseTime } from '../../../helpers/UtilityHelper'
import { SUBTLE } from '../../../styles/foundation/colors'
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography'
import SectionCard from './SectionCard'
import { InfoRow } from './SummaryCards'
import { categoryLabel } from '../ExtraDocsEditor'
import AppointmentHistoryView from '../../appointments/shared/AppointmentHistoryView'

/*
 * PTL documents on Order Details, placed as in the Admin Panel (pages/orders/OrderDetails.js):
 * "Current Appointment" (ABD, documents under the slot details), "Additional Documents" (Non-ABD) and
 * "Previous Appointments (N)". View only; uploads stay in the shipment list actions.
 */

const ABD_DOCS = [
  { keyField: 'appointment_del_invoice_s3_key', label: 'Invoice', docType: 'INVOICE', Icon: ReceiptLongIcon, color: '#1565c0', bg: '#e3f2fd' },
  { keyField: 'appointment_del_po_copy_s3_key', label: 'PO Copy', docType: 'PO_COPY', Icon: DescriptionIcon, color: '#2e7d32', bg: '#e8f5e9' },
  { keyField: 'appointment_del_eway_bill_s3_key', label: 'E-Way Bill', docType: 'EWAY_BILL', Icon: LocalShippingIcon, color: '#e65100', bg: '#fff3e0' },
]
const NON_ABD_DOCS = [
  { keyField: 'invoiceS3Key', label: 'Invoice', docType: 'INVOICE', Icon: ReceiptLongIcon, color: '#1565c0', bg: '#e3f2fd' },
  { keyField: 'poCopyS3Key', label: 'PO Copy', docType: 'PO_COPY', Icon: DescriptionIcon, color: '#2e7d32', bg: '#e8f5e9' },
  { keyField: 'ewayBillS3Key', label: 'E-Way Bill', docType: 'EWAY_BILL', Icon: LocalShippingIcon, color: '#e65100', bg: '#fff3e0' },
]

const btnSx = (on, color, bg) => ({
  py: 0.25, px: 1, fontSize: 11, textTransform: 'none',
  color: on ? color : 'text.disabled',
  borderColor: on ? color : 'divider',
  bgcolor: on ? bg : 'transparent',
  '&:hover': on ? { bgcolor: bg, borderColor: color, filter: 'brightness(0.95)' } : {},
})

/** Opens a document through `docUrlApi` by docType or, for extra documents, by docId. */
function useOpenDoc(awbNumber, docUrlApi) {
  const { enqueueSnackbar } = useSnackbar()
  return async (params) => {
    try {
      const res = await getGeneric(docUrlApi, { awbNumber, ...params })
      const url = res?.data?.responseObj?.url || res?.data?.url
      if (url) window.open(url, '_blank', 'noopener')
      else enqueueSnackbar('Document URL missing', { variant: 'warning' })
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || 'Failed to fetch document', { variant: 'error' })
    }
  }
}

/** "DOCUMENTS" row: Invoice / PO Copy / E-Way Bill (greyed out when not uploaded), then each extra document. */
function DocumentsRow({ data, docs, extras, openDoc }) {
  return (
    <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap' useFlexGap>
      <Typography sx={{ fontFamily: FONT, fontSize: '10px', fontWeight: WEIGHT.semibold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Documents
      </Typography>
      {docs.map(({ keyField, label, docType, Icon, color, bg }) => {
        const has = !!data?.[keyField]
        return (
          <Tooltip key={docType} title={has ? `View ${label}` : `${label} not uploaded`}>
            <span>
              <Button size='small' variant='outlined' disabled={!has} startIcon={<Icon sx={{ fontSize: 14 }} />}
                onClick={() => openDoc({ docType })} sx={btnSx(has, color, bg)}>
                {label}
              </Button>
            </span>
          </Tooltip>
        )
      })}
      {extras.map((xd) => (
        <Tooltip key={xd.docId} title={`View ${xd.docLabel || categoryLabel(xd.docType)}`}>
          <span>
            <Button size='small' variant='outlined' startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}
              onClick={() => openDoc({ docId: xd.docId })} sx={btnSx(true, '#5e35b1', '#ede7f6')}>
              {xd.docLabel || categoryLabel(xd.docType)}
            </Button>
          </span>
        </Tooltip>
      ))}
    </Stack>
  )
}

function ContactsToggle({ visible, onToggle }) {
  return (
    <Button size='small' variant={visible ? 'outlined' : 'contained'} color={visible ? 'inherit' : 'error'}
      startIcon={visible ? <VisibilityOffTwoTone /> : <VisibilityTwoTone />} onClick={onToggle}
      sx={{ textTransform: 'none', fontFamily: FONT, fontSize: SIZE['2xs'] }}>
      {visible ? 'Hide Contacts' : 'Show Contacts'}
    </Button>
  )
}

const LastUpdated = ({ data }) => (data?.lastModifiedDate ? (
  <Typography sx={{ display: 'block', mt: 1.5, fontFamily: FONT, fontSize: '11px', color: SUBTLE }}>
    Last updated · {formatDate(data.lastModifiedDate, 'dd MMM, yyyy HH:mm')}
  </Typography>
) : null)

const Field = ({ label, value, md = 3 }) => (
  <Grid item xs={6} md={md}><InfoRow label={label} value={value || null} /></Grid>
)

/** ABD: the current appointment with its documents (Admin "Current Appointment"). */
export function CurrentAppointmentCard({ order }) {
  const [contactsVisible, setContactsVisible] = useState(false)
  const openDoc = useOpenDoc(order?.awb_number, apiConstants.APPOINTMENT_DELIVERY_DOC_URL)
  if (!order?.appointmentDelivery || !order?.appointmentDeliveryData) return null
  const ad = order.appointmentDeliveryData
  const alt = (v) => (v ? (contactsVisible ? v : maskPhoneNumber(v)) : null)
  const hasAlt = ad.appointment_del_alt_contact_1 || ad.appointment_del_alt_contact_2

  return (
    <SectionCard
      title={`Current Appointment · Attempt #${(order.appointmentDeliveryHistory?.length || 0) + 1}`}
      action={hasAlt ? <ContactsToggle visible={contactsVisible} onToggle={() => setContactsVisible((v) => !v)} /> : null}
    >
      <Grid container spacing={2}>
        <Field label='Delivery Date' value={order.appointment_del_date ? formatDate(order.appointment_del_date, 'dd MMM, yyyy') : null} />
        <Field label='Time Slot' value={ad.appointment_del_start ? `${parseTime(ad.appointment_del_start)} – ${parseTime(ad.appointment_del_end)}` : null} />
        <Field label='PO #' value={ad.appointment_del_po} />
        <Field label='PO Expiry' value={ad.appointment_del_po_expiry ? formatDate(ad.appointment_del_po_expiry, 'dd MMM, yyyy') : null} />
        <Field label='Alt Contact 1' value={alt(ad.appointment_del_alt_contact_1)} />
        <Field label='Alt Contact 2' value={alt(ad.appointment_del_alt_contact_2)} />
        <Field label='Remarks' value={ad.appointment_del_remarks} md={6} />
      </Grid>
      <Divider sx={{ my: 2 }} />
      <DocumentsRow data={ad} docs={ABD_DOCS} extras={ad.appointment_del_extra_docs || ad.extraDocs || []} openDoc={openDoc} />
      <LastUpdated data={ad} />
    </SectionCard>
  )
}

/** Non-ABD (or before the first appointment): documents uploaded on the shipment (Admin "Additional Documents"). */
export function AdditionalDocumentsCard({ order }) {
  const [contactsVisible, setContactsVisible] = useState(false)
  const openDoc = useOpenDoc(order?.awb_number, apiConstants.ADDITIONAL_DOCUMENTS_DOC_URL)
  const ad = order?.additionalDocuments
  if (!ad) return null
  const alt = (v) => (v ? (contactsVisible ? v : maskPhoneNumber(v)) : null)

  return (
    <SectionCard
      title='Additional Documents'
      action={ad.altContact1 || ad.altContact2 ? <ContactsToggle visible={contactsVisible} onToggle={() => setContactsVisible((v) => !v)} /> : null}
    >
      <Grid container spacing={2}>
        <Field label='PO #' value={ad.poNumber} />
        <Field label='PO Expiry' value={ad.poExpiry ? formatDate(ad.poExpiry, 'dd MMM, yyyy') : null} />
        <Field label='Alt Contact 1' value={alt(ad.altContact1)} />
        <Field label='Alt Contact 2' value={alt(ad.altContact2)} />
      </Grid>
      <Divider sx={{ my: 2 }} />
      <DocumentsRow data={ad} docs={NON_ABD_DOCS} extras={ad.extraDocs || []} openDoc={openDoc} />
      <LastUpdated data={ad} />
    </SectionCard>
  )
}

/** Every appointment attempt with its documents (Admin "Previous Appointments (N)"). */
export function PreviousAppointmentsCard({ order }) {
  const count = order?.appointmentDeliveryHistory?.length || 0
  if (!count) return null
  return (
    <SectionCard title={`Previous Appointments (${count})`}>
      <Box><AppointmentHistoryView rowData={order} /></Box>
    </SectionCard>
  )
}
